import * as React from "react";
import { Fieldset, FieldsetProps } from "@bizform/primereact/components/fieldset/Fieldset";
import { Static } from "@bizform/type/decorator/Static.decorator";
import { DomAttribute } from "@bizform/type/model/dom/DomAttribute.model";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementBorderType,
    ComponentElementDisplayMode,
    ComponentElementOverflowType,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { ComponentHelper, Debug } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../../bf_component/AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";
import {DataType} from "@bizform/type/model/model/DataType.model";
import {DefaultLayoutId} from "@bizform/type/model/component_element/DefaultLayoutId.model";


type Props = FieldsetProps & {};

type WrappedCompState = Props & ComponentElementOverflowType & ComponentElementBorderType & {
    useCustomTitle?: boolean;
    titleData?: string;
    subscriptionValue?: string;
    titleStyle?: object;
    contentStyle?: object;
};

type WrappedFormState = {
    onExpand?: string;
    onCollapse?: string;
    onToggle?: string;
    onClick?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Byungmin Choi, Hwansoon Park
 * @class BfFieldset
 * @description BfFieldset component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfFieldset extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_pvCCLRTbg22AyzNk1cTfThrbIMXNZBR",
        category: ComponentCategory.PANEL,
        label: I18n.layout.fieldset.label,
        description: I18n.layout.fieldset.description,
        defaultLayoutId: DefaultLayoutId.ONLYCOMPONENTELEMENT,
        icon: "",
        dataType: [DataType.STRING],
        property: null
    };

    focus(): void {
        // this.subscription(!this.state.wrappedCompState.collapsed);
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            this._needRender = true;
            const newState = ComponentHelper.createStateWithProperties<State, { e: Event, value?: boolean }>(
                property,
                this);
            const n = newState.wrappedCompState;
            if (this.accessibility) {
                n.toggleable = false;
                n.collapsed = false;
            } else {
                n.toggleable = n.toggleable ? true : false;
                n.collapsed = n.collapsed ? true : false;
            }
            this.setState(newState);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    emitFormState(value: string): void {
    }

    subscription(value: string) {
        this._needRender = true;
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                subscriptionValue: value
            }
        };
        this.setState(newState);
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfFieldset)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _fieldset: HTMLElement;
    private _needRender: boolean;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {},
            wrappedFormState: {}
        };

        this._setRef = this._setRef.bind(this);
        this._onToggleEvent = this._onToggleEvent.bind(this);
        this._onExpandEvent = this._onExpandEvent.bind(this);
        this._onCollapseEvent = this._onCollapseEvent.bind(this);
        this._onClickEvent = this._onClickEvent.bind(this);
    }

    componentWillUnmount(): void {
        if (this._fieldset) {
            if (this._fieldset.hasAttribute(DomAttribute.CONTAINER.toString())) {
                this._fieldset.removeAttribute(DomAttribute.CONTAINER.toString());
            }
        }
        this._fieldset = null;
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (this._fieldset && this._needRender) {
            const titleElement = this._fieldset.querySelector("a") as HTMLElement;
            const htmlText = this._fieldset.querySelector(".p-fieldset-legend-text")as HTMLElement;

            if (this.dataType === DataType.STRING && this.state.wrappedCompState.subscriptionValue && this.state.wrappedCompState.subscriptionValue.trim() !== "") {
                htmlText.textContent = this.state.wrappedCompState.subscriptionValue;
            } else {
                if (this.state.wrappedCompState.useCustomTitle) {
                    htmlText.innerHTML = this.state.wrappedCompState.titleData;
                } else {
                    htmlText.textContent = this.state.wrappedCompState.titleData;
                }
            }

            if (titleElement) {
                if (prevState.wrappedCompState.titleStyle) {
                    for (const style in prevState.wrappedCompState.titleStyle) {
                        titleElement.style[style] = "";
                    }
                }
                if (this.state.wrappedCompState.titleStyle) {
                    for (const style in this.state.wrappedCompState.titleStyle) {
                        titleElement.style[style] = this.state.wrappedCompState.titleStyle[style];
                    }
                }
            }
            this._needRender = false;
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    private _callbackSetState(): void {
        if (this.state.wrappedFormState && this.state.wrappedFormState.onToggle) {
            this.emitFormState(this.state.wrappedFormState.onToggle);
        }
    }

    private _onToggleEvent(e): void {
        this.setState({wrappedCompState: {
                ...this.state.wrappedCompState,
                collapsed: e.value
            }}, this._callbackSetState);
    }

    private _onExpandEvent(event): void {
        if (this.state.wrappedFormState && this.state.wrappedFormState.onExpand) {
            this.emitFormState(this.state.wrappedFormState.onExpand);
        }
    }

    private _onCollapseEvent(event): void {
        if (this.state.wrappedFormState && this.state.wrappedFormState.onCollapse) {
            this.emitFormState(this.state.wrappedFormState.onCollapse);
        }
    }

    private _onClickEvent(event): void {
        if (this.state.wrappedFormState && this.state.wrappedFormState.onClick) {
            this.emitFormState(this.state.wrappedFormState.onClick);
        }
    }

    render(): React.ReactNode {
        if (!this.state.show) {
            return null;
        } else {
            return (
                <Fieldset
                    {...this.state.wrappedCompState}
                    key={this.elementId}
                    id={this.elementId}
                    ref={this._setRef}
                >
                    {this._container()}
                </Fieldset>
            );
        }
    }

    private _container() {
        const attr = {};
        attr[DomAttribute.CONTAINER] = "";
        return (
            <div
                { ...attr }
                style={{ ...this.state.wrappedCompState.contentStyle }}
            />
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._fieldset = this.parentDom.querySelector("#" + this.elementId);
            this.readyComponent();
        }
    }
}